import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import React, { useCallback, useMemo, useState } from 'react'

import CreateButton from '@/app/component/atom/create-button'
import MultipleChoice from '@/app/component/atom/filters/multiple-choice'
import SingleChoice from '@/app/component/atom/filters/single-choice'
import Search from '@/app/component/atom/search'
import Tooltip from '@/app/component/atom/tooltip'
import ContentLayout from '@/app/component/layout/content'
import TableToolbar from '@/app/component/layout/table/toolbar'
import { useDeepState } from '@/app/helpers'
import { CAMPAIGN_STATUS_MAP, TEMPLATE_TYPES, TEMPLATES_TO_TITLE } from '@/app/module/campaigns/definitions'
import { CampaignQuery } from '@/app/module/campaigns/types'

type Props = {
  filters: CampaignQuery
  loading: boolean
  page: number

  onCreateCampaignsClick: () => void
  onRefresh: () => void
  onSetFilters: (payload: { filters: CampaignQuery }) => void
}

const CampaignsTableToolbar: React.FC<Props> = ({
  filters,
  loading,
  onCreateCampaignsClick,
  onRefresh,
  onSetFilters,
}) => {
  const [menus, setMenus] = useState<Record<string, Element | undefined>>({})
  const [searchValue, setSearchValue] = useDeepState<string>(filters.search || '')
  const [statusValue, setStatusValue] = useDeepState<string>(getStatusFromFilters(filters) || 'all')
  const [templateValue, setTemplateValue] = useDeepState<string[]>(
    filters.templates ? filters.templates.split(',').filter(Boolean) : [],
  )

  const toggleMenu = useCallback((menu: string, element?: Element) => {
    setMenus((s) => ({
      ...s,
      [menu]: element,
    }))
  }, [])

  const statusOptions = useMemo(
    () => [
      {
        label: 'All',
        value: 'all',
      },
      ...statuses.map((status) => ({
        label: status,
        value: status,
      })),
    ],
    [],
  )
  const templateOptions = useMemo(
    () =>
      TEMPLATE_TYPES.map((template) => ({
        label: TEMPLATES_TO_TITLE[template],
        value: template,
      })),
    [],
  )

  return (
    <ContentLayout>
      <StyledToolbar>
        <Container>
          <div>
            <Search
              disabled={loading}
              label="Search"
              namespace="campaigns"
              tooltipTitle="Search by name"
              searchTerm={searchValue}
              onSubmit={(value: string) => {
                setSearchValue(value)
                onSetFilters({
                  filters: {
                    ...filters,
                    search: value,
                  },
                })
              }}
            />
          </div>
          <Button
            variant="outlined"
            onClick={(e) => toggleMenu('templateMenu', e.currentTarget)}
            endIcon={<ArrowDropDownIcon />}
            startIcon={<FilterListIcon />}
          >
            Types
          </Button>
          <Button
            variant="outlined"
            onClick={(e) => toggleMenu('statusMenu', e.currentTarget)}
            endIcon={<ArrowDropDownIcon />}
            startIcon={<FilterListIcon />}
          >
            Status
          </Button>
          <Button
            variant="outlined"
            onClick={(e) => toggleMenu('archivedMenu', e.currentTarget)}
            endIcon={<ArrowDropDownIcon />}
            startIcon={<FilterListIcon />}
          >
            Archived
          </Button>
          <CreateButton onClick={onCreateCampaignsClick}>New campaign</CreateButton>

          <Menu
            id="campaign-template-menu"
            open={!!menus.templateMenu}
            onClose={() => toggleMenu('templateMenu')}
            anchorEl={menus.templateMenu}
          >
            <MultipleChoice
              options={templateOptions}
              value={templateValue}
              onChange={(v) => {
                setTemplateValue(v)

                onSetFilters({
                  filters: {
                    ...filters,
                    templates: v?.join(','),
                  },
                })
              }}
            />
          </Menu>
          <Menu
            id="campaign-status-menu"
            open={!!menus.statusMenu}
            onClose={() => toggleMenu('statusMenu')}
            anchorEl={menus.statusMenu}
          >
            <SingleChoice
              name="status-filters"
              options={statusOptions}
              value={statusValue}
              onChange={(v) => {
                setStatusValue(v)
                const newFilters = campaignStatusMap[v]

                if (!newFilters) {
                  onSetFilters({
                    filters: {
                      archived: filters.archived,
                      open: filters.open,
                      page: filters.page,
                      size: filters.size,
                      templates: filters.templates,
                      search: filters.search,
                    },
                  })
                } else {
                  onSetFilters({
                    filters: {
                      archived: filters.archived,
                      open: filters.open,
                      page: filters.page,
                      size: filters.size,
                      templates: filters.templates,
                      search: filters.search,
                      ...(newFilters as any),
                    },
                  })
                }
              }}
            />
          </Menu>
          <Menu
            id="campaign-archived-menu"
            open={!!menus.archivedMenu}
            onClose={() => toggleMenu('archivedMenu')}
            anchorEl={menus.archivedMenu}
          >
            <div>
              <StyledMenuItem disableGutters>
                <StyledFormControlLabel
                  control={
                    <Checkbox
                      checked={filters.archived === 'true'}
                      onChange={(e) => {
                        onSetFilters({
                          filters: {
                            ...filters,
                            archived: e.target.checked ? 'true' : 'false',
                          },
                        })
                      }}
                      value="archived"
                      color="primary"
                    />
                  }
                  label="Include archived"
                />
              </StyledMenuItem>
            </div>
          </Menu>
          <div>
            <Tooltip title="Reload campaigns">
              <IconButton onClick={onRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </div>
        </Container>
      </StyledToolbar>
    </ContentLayout>
  )
}

const StyledToolbar = styled(TableToolbar)({
  paddingLeft: '0 !important',
  paddingRight: '0 !important',
})

const Container = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
})

const StyledMenuItem = styled(MenuItem)({
  padding: 0,
})

const StyledFormControlLabel = styled(FormControlLabel)({
  margin: 0,
  padding: '6px 16px 6px 5px',
  width: '100%',
})

const campaignStatusMap: Record<string, Partial<CampaignQuery>> = CAMPAIGN_STATUS_MAP

const statuses = ['Draft', 'Launched', 'Registering', 'Stopped']

const getStatusFromFilters = (filters: CampaignQuery) => {
  for (let i = 0; i < statuses.length; i += 1) {
    const status = statuses[i]
    const filterMap = campaignStatusMap[status]

    const keys = Object.keys(filterMap) as Array<keyof CampaignQuery>

    let matched = true

    for (let j = 0; j < keys.length; j += 1) {
      const key = keys[j]
      if (filters[key] !== filterMap[key]) {
        matched = false
        break
      }
    }
    if (matched) {
      return status
    }
  }

  return ''
}

export default CampaignsTableToolbar
